import React, { useState, useEffect, useMemo } from 'react';
import { Package, Save, Minus, X, AlertCircle, Info, Calendar, Users } from 'lucide-react';
import { toast } from 'react-toastify';
import authUtils from '../utils/authUtils';

// Utility function để generate unique ID
const generateUniqueId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
    result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));
    for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

// Parse number safely
const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : Math.max(0, num);
};

const parseDecimal = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.max(0, num);
};

// Format number with thousand separator
const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    const num = parseInt(number, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('vi-VN');
};

// Format date to YYYY-MM-DD
const formatDateForInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Format date to Vietnamese display
const formatDateDisplay = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const PhanBoBaoBiEmTongThe = ({ onClose, onSuccess, packagingData, phanBoData }) => {
    const [teamList, setTeamList] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(formatDateForInput(new Date()));

    // Tính tổng THỰC NHẬN BBE từ tất cả các xe
    const totalStats = useMemo(() => {
        const totalThucNhanBBE = packagingData.reduce((sum, record) => {
            return sum + parseNumber(record['THỰC NHẬN BBE (KG)']);
        }, 0);

        const totalAllocatedBBE = phanBoData
            .filter(pb => pb['TÊN HÀNG'] === 'Bao bì em' && !pb.ID_NHAPBAOBI)
            .reduce((sum, pb) => sum + parseNumber(pb['KHỐI LƯỢNG']), 0);

        return {
            totalThucNhanBBE,
            totalAllocatedBBE,
            totalRemaining: totalThucNhanBBE - totalAllocatedBBE
        };
    }, [packagingData, phanBoData]);

    // Tính toán số lượng còn lại
    const remaining = useMemo(() => {
        const currentAllocated = allocations.reduce((sum, a) => sum + parseNumber(a.totalQuantity), 0);
        return totalStats.totalRemaining - currentAllocated;
    }, [allocations, totalStats]);

    useEffect(() => {
        fetchTeamData();
    }, []);

    // Tự động thêm tất cả các tổ vào allocations khi teamList được load
    useEffect(() => {
        if (teamList.length > 0 && allocations.length === 0) {
            const allTeamAllocations = teamList.map((teamName, index) => ({
                id: `${Date.now()}_${index}`,
                teamName: teamName,
                soLong: 0,
                khoiLuongTheoLong: 900,
                totalQuantity: 0
            }));
            setAllocations(allTeamAllocations);
        }
    }, [teamList]);

    const fetchTeamData = async () => {
        try {
            setLoading(true);
            const teamResponse = await authUtils.apiRequest('TO_PBNS', 'Find', {});

            const uniqueTeams = [...new Set(
                teamResponse
                    .filter(t => t['NHÓM'] === 'Bao bì em')
                    .map(t => t['TỔ'])
            )];

            setTeamList(uniqueTeams);
        } catch (error) {
            console.error('Error fetching team data:', error);
            toast.error('Lỗi khi tải dữ liệu tổ');
        } finally {
            setLoading(false);
        }
    };

    const addAllocation = (teamName) => {
        const existingAllocation = allocations.find(a => a.teamName === teamName);
        if (existingAllocation) {
            toast.warning(`Tổ ${teamName} đã có trong danh sách phân bổ`);
            return;
        }

        const newAllocation = {
            id: Date.now().toString(),
            teamName: teamName,
            soLong: 0,
            khoiLuongTheoLong: 900,
            totalQuantity: 0
        };
        setAllocations([...allocations, newAllocation]);
        toast.success(`Đã thêm tổ ${teamName}`);
    };

    const updateSoLong = (allocationId, soLong) => {
        const numSoLong = parseDecimal(soLong);
        setAllocations(prev =>
            prev.map(allocation => {
                if (allocation.id === allocationId) {
                    const totalQuantity = parseNumber(Math.round(numSoLong * parseNumber(allocation.khoiLuongTheoLong)));
                    return {
                        ...allocation,
                        soLong: numSoLong,
                        totalQuantity: totalQuantity
                    };
                }
                return allocation;
            })
        );
    };

    const updateKhoiLuongTheoLong = (allocationId, khoiLuong) => {
        const numKhoiLuong = parseNumber(khoiLuong);
        setAllocations(prev =>
            prev.map(allocation => {
                if (allocation.id === allocationId) {
                    const totalQuantity = parseNumber(Math.round(parseDecimal(allocation.soLong) * numKhoiLuong));
                    return {
                        ...allocation,
                        khoiLuongTheoLong: numKhoiLuong,
                        totalQuantity: totalQuantity
                    };
                }
                return allocation;
            })
        );
    };

    const removeAllocation = (allocationId) => {
        setAllocations(prev => prev.filter(a => a.id !== allocationId));
    };

    const handleSave = async () => {
        try {
            setLoading(true);

            // Bỏ kiểm tra remaining < 0, chỉ cảnh báo người dùng
            const validAllocations = allocations.filter(a => a.totalQuantity > 0);
            if (validAllocations.length === 0) {
                toast.warning('Vui lòng nhập số lồng để tính khối lượng phân bổ');
                setLoading(false);
                return;
            }

            // Dữ liệu cho bảng PHANBOBAOBI
            // Tạo IDPB trước để dùng cho cả PHANBOBAOBI và NHAPBAOBI_CT
            const allocationsWithIds = validAllocations.map(allocation => ({
                ...allocation,
                generatedIdpb: generateUniqueId()
            }));

            const phanBoRecords = allocationsWithIds.map(allocation => ({
                IDPB: allocation.generatedIdpb,
                NGÀY: selectedDate,
                'TÊN HÀNG': 'Bao bì em',
                TỔ: allocation.teamName,
                'SỐ DÂY': '',
                'SỐ LỒNG': allocation.soLong,
                'KHỐI LƯỢNG THEO LỒNG': allocation.khoiLuongTheoLong,
                'KHỐI LƯỢNG': allocation.totalQuantity
            }));

            // Dữ liệu cho bảng NHAPBAOBI_CT
            const nhapBaoBiCTRecords = allocationsWithIds.map(allocation => ({
                ID_CT: generateUniqueId(),
                IDPB_REF: allocation.generatedIdpb,
                IDCBB_REF: '',
                NGÀY: selectedDate,
                'TÊN HÀNG': 'Bao bì em',
                'NGHIỆP VỤ': 'Nhập',
                'TỔ': allocation.teamName,
                'SỐ LỒNG': allocation.soLong,
                'KHỐI LƯỢNG/ LỒNG': allocation.khoiLuongTheoLong,
                'KHỐI LƯỢNG (KG)': allocation.totalQuantity
            }));

            // Lưu vào bảng PHANBOBAOBI
            await authUtils.apiRequest('PHANBOBAOBI', 'Add', { Rows: phanBoRecords });

            // Lưu vào bảng NHAPBAOBI_CT
            await authUtils.apiRequest('NHAPBAOBI_CT', 'Add', { Rows: nhapBaoBiCTRecords });

            toast.success(`Đã phân bổ thành công ${phanBoRecords.length} tổ với tổng ${formatNumber(validAllocations.reduce((sum, a) => sum + a.totalQuantity, 0))} KG`);
            onSuccess?.();
            onClose();

        } catch (error) {
            console.error('Error saving allocation:', error);
            toast.error('Lỗi khi lưu phân bổ: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
                    <div className="flex justify-between items-center mb-3">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Package className="h-6 w-6 text-pink-600" />
                                Phân bổ bao bì em
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Phân bổ từ bãi cho bao bì em
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Summary Info - Compact */}
                    <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-3">
                        <div className="grid grid-cols-5 gap-3">
                            <div>
                                <div className="text-xs text-gray-600 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    Ngày phân bổ
                                </div>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="mt-1 w-full px-2 py-1 border border-pink-300 rounded text-sm focus:ring-pink-500 focus:border-pink-500"
                                />
                            </div>
                            <div>
                                <div className="text-xs text-gray-600">Tổng bao bì em</div>
                                <div className="text-base font-bold text-pink-600">
                                    {formatNumber(totalStats.totalThucNhanBBE)} KG
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-600">Đã phân bổ</div>
                                <div className="text-base font-bold text-green-600">
                                    {formatNumber(totalStats.totalAllocatedBBE)} KG
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-600">Đang phân bổ</div>
                                <div className="text-base font-bold text-orange-600">
                                    {formatNumber(allocations.reduce((sum, a) => sum + parseNumber(a.totalQuantity), 0))} KG
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-600">Còn lại</div>
                                <div className={`text-base font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatNumber(remaining)} KG
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
                            <span className="ml-2 text-gray-600">Đang tải dữ liệu...</span>
                        </div>
                    ) : (
                        <>
                            {/* Team Selection - Compact */}
                            <div className="mb-4">
                                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    Chọn tổ để phân bổ
                                </h3>
                                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2">
                                    {teamList.map((teamName, teamIdx) => {
                                        const isAdded = allocations.some(a => a.teamName === teamName);
                                        return (
                                            <button
                                                key={teamName}
                                                type="button"
                                                data-bbe-team-idx={teamIdx}
                                                data-bbe-team-name={teamName}
                                                onClick={() => addAllocation(teamName)}
                                                aria-disabled={isAdded}
                                                onKeyDown={(e) => {
                                                    if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                                                        e.preventDefault();

                                                        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                                                            const prevBtn = document.querySelector(
                                                                `button[data-bbe-team-idx="${teamIdx - 1}"]`
                                                            );
                                                            if (prevBtn) prevBtn.focus();
                                                        }

                                                        if (e.key === 'ArrowRight') {
                                                            const nextBtn = document.querySelector(
                                                                `button[data-bbe-team-idx="${teamIdx + 1}"]`
                                                            );
                                                            if (nextBtn) nextBtn.focus();
                                                        }

                                                        if (e.key === 'ArrowDown') {
                                                            const targetInput = document.querySelector(
                                                                `input[data-bbe-team-name="${teamName}"][data-bbe-alloc-col="0"]`
                                                            ) || document.querySelector(
                                                                'input[data-bbe-alloc-row="0"][data-bbe-alloc-col="0"]'
                                                            );
                                                            if (targetInput) {
                                                                targetInput.focus();
                                                                targetInput.select();
                                                            }
                                                        }
                                                    }
                                                }}
                                                className={`px-3 py-2 text-sm border rounded-lg transition-colors text-center font-medium ${isAdded
                                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                                    : 'border-gray-300 hover:bg-pink-50 hover:border-pink-300'
                                                    }`}
                                            >
                                                {teamName}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Current Allocations - Compact Table */}
                            {allocations.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium mb-2">Phân bổ mới</h3>
                                    <div className="border border-pink-200 rounded-lg overflow-hidden">
                                        <table className="min-w-full divide-y divide-pink-200">
                                            <thead className="bg-pink-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Tổ</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Số lồng</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">KL/lồng (KG)</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Khối lượng (KG)</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-700">Xóa</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-pink-100">
                                                {allocations.map((allocation, allocationIdx) => (
                                                    <tr key={allocation.id} className="hover:bg-pink-50">
                                                        <td className="px-3 py-2 text-sm font-medium text-gray-800">
                                                            {allocation.teamName}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                placeholder="0"
                                                                value={allocation.soLong || ''}
                                                                data-bbe-alloc-row={allocationIdx}
                                                                data-bbe-alloc-col="0"
                                                                data-bbe-team-name={allocation.teamName}
                                                                onChange={(e) => updateSoLong(allocation.id, e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (['-', '+', 'e', 'E'].includes(e.key)) {
                                                                        e.preventDefault();
                                                                    }
                                                                    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                                        e.preventDefault();
                                                                        const currentRow = allocationIdx;
                                                                        const currentCol = 0;
                                                                        let targetRow = currentRow;
                                                                        let targetCol = currentCol;

                                                                        if (e.key === 'ArrowUp') targetRow = currentRow - 1;
                                                                        if (e.key === 'ArrowDown') targetRow = currentRow + 1;
                                                                        if (e.key === 'ArrowLeft') targetCol = currentCol - 1;
                                                                        if (e.key === 'ArrowRight') targetCol = currentCol + 1;
                                                                        if (targetCol < 0) targetCol = 0;
                                                                        if (targetCol > 1) targetCol = 1;

                                                                        if (e.key === 'ArrowUp' && currentRow === 0) {
                                                                            const teamBtn = document.querySelector(
                                                                                `button[data-bbe-team-name="${allocation.teamName}"]`
                                                                            ) || document.querySelector(
                                                                                'button[data-bbe-team-idx="0"]'
                                                                            );
                                                                            if (teamBtn) {
                                                                                teamBtn.focus();
                                                                                return;
                                                                            }
                                                                        }

                                                                        const targetInput = document.querySelector(
                                                                            `input[data-bbe-alloc-row="${targetRow}"][data-bbe-alloc-col="${targetCol}"]`
                                                                        );
                                                                        if (targetInput) {
                                                                            targetInput.focus();
                                                                            targetInput.select();
                                                                        }
                                                                    }
                                                                }}
                                                                className="w-20 px-2 py-1 border border-pink-300 rounded text-sm focus:ring-pink-500 focus:border-pink-500"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                min="0"
                                                                placeholder="900"
                                                                value={allocation.khoiLuongTheoLong || ''}
                                                                data-bbe-alloc-row={allocationIdx}
                                                                data-bbe-alloc-col="1"
                                                                data-bbe-team-name={allocation.teamName}
                                                                onChange={(e) => updateKhoiLuongTheoLong(allocation.id, e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (['-', '+', 'e', 'E'].includes(e.key)) {
                                                                        e.preventDefault();
                                                                    }
                                                                    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                                        e.preventDefault();
                                                                        const currentRow = allocationIdx;
                                                                        const currentCol = 1;
                                                                        let targetRow = currentRow;
                                                                        let targetCol = currentCol;

                                                                        if (e.key === 'ArrowUp') targetRow = currentRow - 1;
                                                                        if (e.key === 'ArrowDown') targetRow = currentRow + 1;
                                                                        if (e.key === 'ArrowLeft') targetCol = currentCol - 1;
                                                                        if (e.key === 'ArrowRight') targetCol = currentCol + 1;
                                                                        if (targetCol < 0) targetCol = 0;
                                                                        if (targetCol > 1) targetCol = 1;

                                                                        if (e.key === 'ArrowUp' && currentRow === 0) {
                                                                            const teamBtn = document.querySelector(
                                                                                `button[data-bbe-team-name="${allocation.teamName}"]`
                                                                            ) || document.querySelector(
                                                                                'button[data-bbe-team-idx="0"]'
                                                                            );
                                                                            if (teamBtn) {
                                                                                teamBtn.focus();
                                                                                return;
                                                                            }
                                                                        }

                                                                        const targetInput = document.querySelector(
                                                                            `input[data-bbe-alloc-row="${targetRow}"][data-bbe-alloc-col="${targetCol}"]`
                                                                        );
                                                                        if (targetInput) {
                                                                            targetInput.focus();
                                                                            targetInput.select();
                                                                        }
                                                                    }
                                                                }}
                                                                className="w-20 px-2 py-1 border border-pink-300 rounded text-sm focus:ring-pink-500 focus:border-pink-500"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <span className="text-sm font-bold text-pink-600">
                                                                {formatNumber(allocation.totalQuantity)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            <button
                                                                onClick={() => removeAllocation(allocation.id)}
                                                                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                title="Xóa"
                                                            >
                                                                <Minus className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {allocations.length === 0 && !loading && (
                                <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                                    <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">Đang tải danh sách tổ...</p>
                                </div>
                            )}

                            {/* Warning */}
                            {remaining < 0 && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center text-red-800">
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        <span className="text-sm font-medium">
                                            Cảnh báo: Số lượng phân bổ vượt quá số lượng còn lại!
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Info Note - Compact */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-start">
                                    <Info className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                                    <div className="text-xs text-blue-800">
                                        <p className="font-medium">Lưu ý: Khối lượng phân bổ = Số lồng × KL/lồng (mặc định 900 KG)</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Đóng
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading || allocations.length === 0} // Loại bỏ điều kiện remaining < 0
                            className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                    Đang lưu...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Phân bổ {allocations.length} tổ
                                </>
                            )}
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default PhanBoBaoBiEmTongThe;
